import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as drawerActions from '../../actions/drawerActions';
import * as basicAnalyticsActions from '../../actions/classroom/basicAnalyticsActions';
import * as classroomApi from '../../services/api/classroomApi';
import * as groupApi from '../../services/api/groupApi';
import { I18n } from 'react-redux-i18n-lite';
import { DrawerCommonPanelCustom } from '../classroom/students/AddUsers/AddUserComponents';
import UserView from '../classroom/students/AddUsers/UserView';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';

class SelectStudentsForAnalytics extends Component {
  state = {
    query: '',
    selectedUsers: [],
    users: [],
    allUsers: [],
    groups: [],
    selectedGroupId: '__all__',
    isLoading: false,
    isLoadingGroups: false
  };

  componentDidMount() {
    this.loadStudents();
    this.loadGroups();
  }

  loadGroups = async () => {
    const { idClassroom } = this.props;
    if (!idClassroom) return;
    this.setState({ isLoadingGroups: true });
    try {
      const response = await classroomApi.getGroups(idClassroom);
      if (response && response.data && Array.isArray(response.data)) {
        this.setState({ groups: response.data, isLoadingGroups: false });
      } else {
        this.setState({ isLoadingGroups: false });
      }
    } catch (err) {
      this.setState({ isLoadingGroups: false });
    }
  };

  loadStudents = async () => {
    const { idClassroom } = this.props;
    if (!idClassroom) return;

    this.setState({ isLoading: true });
    try {
      const response = await classroomApi.getStudents(idClassroom);
      if (response && response.data) {
        const formattedUsers = response.data.map(student => ({
          ...student,
          _id: student.id || student._id,
          id: student.id || student._id
        }));

        const { selectedStudentIds } = this.props;
        let restoredSelectedUsers = [];
        if (selectedStudentIds && selectedStudentIds.length > 0) {
          restoredSelectedUsers = formattedUsers.filter(user =>
            selectedStudentIds.includes(user._id || user.id)
          );
        }

        this.setState({
          users: formattedUsers,
          allUsers: formattedUsers,
          selectedUsers: restoredSelectedUsers,
          isLoading: false
        });
      }
    } catch (err) {
      console.error('Error loading students:', err);
      this.setState({ isLoading: false });
    }
  };

  handlePrimaryButtonClick = () => {
    const { sectionId, date, basicAnalyticsActions, drawerActions, selectedStudentIds } = this.props;

    const studentIds = selectedStudentIds && selectedStudentIds.length > 0
      ? selectedStudentIds
      : null;

    basicAnalyticsActions.fetchBasicAnalyticsData(sectionId, date, studentIds);
    drawerActions.closeDrawerAndRemoveContent();
  };

  handleClear = () => {
    const { basicAnalyticsActions } = this.props;
    basicAnalyticsActions.setSelectedStudents([]);
    this.setState({ selectedUsers: [], selectedGroupId: '__all__' });
  };

  handleOnChange = query => {
    const { allUsers } = this.state;
    if (!query || query.trim() === '') {
      this.setState({ query: '', users: allUsers });
      return;
    }

    const searchQuery = query.toLowerCase();
    const filteredUsers = allUsers.filter(student => {
      const name = (student.name || student.username || '').toLowerCase();
      const email = (student.email || '').toLowerCase();
      return name.includes(searchQuery) || email.includes(searchQuery);
    });

    this.setState({ query, users: filteredUsers });
  };

  handleOnUserClick = user => {
    const { selectedUsers } = this.state;
    const { basicAnalyticsActions } = this.props;
    const userId = user._id || user.id;
    const isSelected = selectedUsers.some(u => (u._id || u.id) === userId);

    const updatedUsers = isSelected
      ? selectedUsers.filter(u => (u._id || u.id) !== userId)
      : [...selectedUsers, user];

    basicAnalyticsActions.setSelectedStudents(updatedUsers.map(u => u._id || u.id));
    this.setState({ selectedUsers: updatedUsers, selectedGroupId: '__all__' });
  };

  handleGroupChange = async event => {
    const groupId = event.target.value;
    const { allUsers } = this.state;
    const { basicAnalyticsActions } = this.props;

    this.setState({ selectedGroupId: groupId || '__all__' });

    if (!groupId || groupId === '__all__') {
      basicAnalyticsActions.setSelectedStudents([]);
      this.setState({ selectedUsers: [] });
      return;
    }

    try {
      const response = await groupApi.getStudents(groupId);
      if (response && response.data && Array.isArray(response.data)) {
        const studentIds = response.data.map(s => s.id || s._id).filter(Boolean);
        basicAnalyticsActions.setSelectedStudents(studentIds);
        const selectedUsers = allUsers.filter(u =>
          studentIds.includes(u._id || u.id)
        );
        this.setState({ selectedUsers });
      }
    } catch (err) {
      this.setState({ selectedGroupId: '__all__' });
    }
  };

  render() {
    const { selectedUsers, users, groups, selectedGroupId, isLoadingGroups } = this.state;
    const { drawerActions, selectedStudentIds } = this.props;
    const selectionCount = (selectedStudentIds && selectedStudentIds.length) || 0;
    const hasSelection = selectionCount > 0;
    const buttonText = hasSelection
      ? `${I18n.t('common.add')} (${selectionCount})`
      : I18n.t('common.add');

    const style = hasSelection
      ? 'multiple-selection-panel show-menu'
      : 'multiple-selection-panel hidden-menu';

    return (
      <DrawerCommonPanelCustom
        closePanel={drawerActions.closeDrawerAndRemoveContent}
        hideRowButtons={false}
        panelHeaderTitle={I18n.t('analytics.filterStudents.panelTitle')}
        showCancelButton={true}
        handleSecondaryButtonClick={this.handleClear}
        secondaryButtonText="common.clear"
        cancelButtonDanger={true}
        primaryButtonText={buttonText}
        onPrimaryButtonClick={this.handlePrimaryButtonClick}
        data-cy="selectStudentsForAnalyticsDrawer"
      >
        <FormControl fullWidth size="small" style={{ marginBottom: 24, minWidth: 200 }}>
          <InputLabel id="analytics-filter-group-label" shrink>
            {I18n.t('analytics.filterStudents.groupLabel')}
          </InputLabel>
          <Select
            labelId="analytics-filter-group-label"
            value={selectedGroupId || '__all__'}
            onChange={this.handleGroupChange}
            disabled={isLoadingGroups}
            renderValue={v => {
              if (!v || v === '__all__') return I18n.t('analytics.filterStudents.allStudents');
              const g = groups.find(gr => gr.id === v);
              return g ? groupApi.getGroupName(g) : I18n.t('analytics.filterStudents.allStudents');
            }}
            data-cy="selectStudentsForAnalyticsGroupSelect"
          >
            <MenuItem value="__all__" data-cy="analyticsGroupOptionAll">
              {I18n.t('analytics.filterStudents.allStudents')}
            </MenuItem>
            {groups.map(group => (
              <MenuItem key={group.id} value={group.id} data-cy={`analyticsGroupOption-${group.id}`}>
                {groupApi.getGroupName(group)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <UserView
          title={I18n.t('analytics.filterStudents.selectTitle')}
          users={users}
          selectedUsers={selectedUsers}
          showSearchArea={true}
          hasMoreItems={false}
          resetPageNumber={0}
          events={{
            onUserClick: user => this.handleOnUserClick(user),
            onLoadMoreItems: () => {},
            onSearchChange: query => this.handleOnChange(query)
          }}
          className={{
            userList: style
          }}
        />
      </DrawerCommonPanelCustom>
    );
  }
}

SelectStudentsForAnalytics.propTypes = {
  drawerActions: PropTypes.object,
  basicAnalyticsActions: PropTypes.object,
  idClassroom: PropTypes.string,
  sectionId: PropTypes.string,
  date: PropTypes.object,
  selectedStudentIds: PropTypes.array
};

const mapDispatchToProps = dispatch => ({
  drawerActions: bindActionCreators(drawerActions, dispatch),
  basicAnalyticsActions: bindActionCreators(basicAnalyticsActions, dispatch)
});

const mapStateToProps = state => ({
  idClassroom: state.router.params.idClassroom,
  sectionId: state.classrooms.selectedClassroom.classroom.sectionId,
  date: state.basicAnalytics.basicAnalyticsDate,
  selectedStudentIds: state.basicAnalytics.selectedStudentIds
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectStudentsForAnalytics);
